import {
  createContext,
  createElement,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { BfrsProvider } from "@/components/ui/providers/BfrsProvider";
import { adoptBfrsStyles, ensureDocumentStyles } from "./shadowStyles";

// True when the current React subtree is already rendered inside a bfrs shadow
// scope. Nested library components read this and render normally, so composition
// (e.g. FormField -> Input, PageHeader actions={<Button/>}) stays inside ONE shadow
// root rather than nesting a shadow per component.
const BfrsShadowScopeContext = createContext(false);

/**
 * Hosts a single shadow root and renders `children` into it (with the library
 * stylesheet adopted) — the building block for per-component auto-isolation.
 */
function AutoShadowHost({ children }: { children: ReactNode }) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [mount, setMount] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    adoptBfrsStyles(shadow);
    ensureDocumentStyles();
    let node = shadow.querySelector<HTMLElement>("[data-bfrs-shadow-mount]");
    if (!node) {
      node = document.createElement("span");
      node.setAttribute("data-bfrs-shadow-mount", "");
      node.style.display = "contents";
      shadow.appendChild(node);
    }
    setMount(node);
  }, []);

  return (
    // display:contents so the host generates no box — the shadow content lays out
    // in the parent flow exactly as the unwrapped component would.
    <span ref={hostRef} style={{ display: "contents" }} data-bfrs-shadow-host="">
      {mount
        ? createPortal(
            <BfrsShadowScopeContext.Provider value={true}>
              <BfrsProvider style={{ display: "contents" }}>{children}</BfrsProvider>
            </BfrsShadowScopeContext.Provider>,
            mount
          )
        : null}
    </span>
  );
}

/**
 * Wraps a component so a top-level instance renders inside its own shadow root
 * (isolated from outside CSS), while instances nested inside another bfrs shadow
 * scope render normally. Ref, displayName and any attached statics are preserved.
 */
type ShadowableComponent = ComponentType<Record<string, unknown>>;

export function withAutoShadow<C extends ShadowableComponent>(Component: C): C {
  const AutoShadowed = forwardRef<unknown, Record<string, unknown>>((props, ref) => {
    const alreadyScoped = useContext(BfrsShadowScopeContext);
    const element = createElement(Component, { ...props, ref });
    if (alreadyScoped) return element;
    return <AutoShadowHost>{element}</AutoShadowHost>;
  });

  const named = Component as { displayName?: string; name?: string };
  AutoShadowed.displayName = `AutoShadow(${named.displayName ?? named.name ?? "Component"})`;
  // Copy any attached statics (e.g. compound sub-components), but NOT React
  // internals — `Object.assign(wrapper, forwardRefComponent)` would overwrite the
  // wrapper's own render/$$typeof and bypass the shadow entirely.
  const reactInternals = new Set(["$$typeof", "render", "type", "compare", "defaultProps", "propTypes", "displayName"]);
  const source = Component as unknown as Record<string, unknown>;
  const target = AutoShadowed as unknown as Record<string, unknown>;
  for (const key of Object.keys(source)) {
    if (!reactInternals.has(key)) target[key] = source[key];
  }
  return AutoShadowed as unknown as C;
}

const isReactComponent = (value: unknown): value is ShadowableComponent => {
  if (typeof value === "function") return true; // function & forwardRef render fns
  // forwardRef/memo objects
  return (
    typeof value === "object" &&
    value !== null &&
    "$$typeof" in (value as Record<string, unknown>) &&
    ("render" in (value as Record<string, unknown>) || "type" in (value as Record<string, unknown>))
  );
};

/**
 * Returns a copy of a module's exports with the named leaf components auto-shadowed
 * and everything else passed through unchanged. Only names in `include` are
 * wrapped — container / overlay / layout components are intentionally excluded
 * because they render the consumer's own children, which must NOT be trapped in
 * (and isolated by) a shadow root.
 */
export function autoShadowModule<T extends Record<string, unknown>>(mod: T, include: ReadonlySet<string>): T {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(mod)) {
    const value = mod[key];
    out[key] = include.has(key) && isReactComponent(value) ? withAutoShadow(value) : value;
  }
  return out as T;
}

export { BfrsShadowScopeContext };
